/**
 * Phép tính thuần giữa biểu mẫu và hợp đồng dây của module `bao-cao`: không gọi HTTP, không dựng chuỗi
 * hiển thị — trả mã lỗi để view tự dịch. Server vẫn là chốt cuối; ở đây chặn sớm những lỗi chắc chắn.
 *
 * Hai nhóm:
 * - **Phân hệ**: bản nháp của `PUT /modules/{moduleId}`. PUT ghi đè trọn gói, khối vắng là xoá khối, nên
 *   cả trang sửa **một** bản nháp và gửi đủ mọi khối mỗi lần lưu.
 * - **Tài liệu**: thân `POST`/`PATCH /documents`. Tài liệu có tệp **không bao giờ** được gửi `url` trừ khi
 *   người dùng chủ động đổi sang liên kết ngoài — có `url` là backend bỏ tệp đang gắn.
 */
import type {
  AccentName,
  Category,
  DocType,
  DocumentFields,
  DocumentItem,
  Hero,
  IconName,
  ModulePayload,
  Rank,
  Stat,
  TepChon,
  Trend,
  UpdateModulePayload,
} from '../types.ts';

// ─────────────────────────── Phân hệ ───────────────────────────

/** Thẻ chủ đạo đang sửa: đường xu hướng giữ dạng chữ người dùng gõ, chỉ đổi sang số lúc lưu. */
export interface HeroNhap {
  label: string;
  value: string;
  caption: string;
  delta: string;
  trend: Trend;
  /** Ví dụ `7.1, 7.9, 8.42` hoặc `7,1; 7,9; 8,42`. */
  sparkChu: string;
}

export interface StatNhap {
  id: string;
  label: string;
  value: string;
  delta: string;
  trend: Trend;
  accentName: AccentName;
  iconName: IconName;
}

export interface BanNhapPhanHe {
  title: string;
  documentsTitle: string;
  hero: HeroNhap | null;
  stats: StatNhap[];
  rank: Rank | null;
}

/** Số điểm tối thiểu của đường xu hướng (bộ gác hợp đồng của backend). */
export const SPARK_TOI_THIEU = 2;

/** Đường xu hướng ra chữ: `[7.1, 7.9]` → `7.1, 7.9` — dạng `docSpark` đọc lại được. */
export function vietSpark(ds: readonly number[]): string {
  return ds.join(', ');
}

/**
 * Chữ → dãy số; `null` khi có một điểm không phải số.
 *
 * Có `;` thì `;` ngăn cách các điểm và `,` là dấu thập phân kiểu Việt (`7,1; 8,42`). Không có `;` thì `,`
 * ngăn cách và `.` là dấu thập phân (`7.1, 8.42`), như ô nhập của native. Chuỗi rỗng là dãy rỗng.
 */
export function docSpark(chu: string): number[] | null {
  const s = chu.trim();
  if (s === '') return [];
  const kieuViet = s.includes(';');
  const manh = s.split(kieuViet ? ';' : ',').map((x) => x.trim());
  const ra: number[] = [];
  for (const m of manh) {
    if (m === '') return null;
    const chuan = kieuViet ? m.replace(/\./g, '').replace(',', '.') : m;
    if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(chuan)) return null;
    ra.push(Number(chuan));
  }
  return ra;
}

function heroNhap(h: Hero): HeroNhap {
  return {
    label: h.label,
    value: h.value,
    caption: h.caption ?? '',
    delta: h.delta ?? '',
    trend: h.trend,
    sparkChu: vietSpark(h.spark ?? []),
  };
}

/** Payload máy chủ → bản nháp. Lấy theo danh sách khoá, không sao chép cả object. */
export function tuPayload(p: ModulePayload): BanNhapPhanHe {
  return {
    title: p.title,
    documentsTitle: p.documentsTitle ?? '',
    hero: p.hero ? heroNhap(p.hero) : null,
    stats: (p.stats ?? []).map((s) => ({ ...s, delta: s.delta ?? '' })),
    rank: p.rank ? { title: p.rank.title, rows: p.rank.rows.map((r) => ({ ...r })) } : null,
  };
}

/**
 * Bản nháp của phân hệ chưa có số liệu (`GET` trả `404`). `title` bắt buộc ở PUT, nên điền sẵn tên phân
 * hệ để lần lưu đầu không trả `400` trên một biểu mẫu người dùng chưa kịp sửa.
 */
export function banNhapMoi(title: string): BanNhapPhanHe {
  return { title, documentsTitle: '', hero: null, stats: [], rank: null };
}

export function heroMoi(): HeroNhap {
  return { label: '', value: '', caption: '', delta: '', trend: 'flat', sparkChu: '' };
}

/** Mã chỉ số mới chưa trùng với mã nào đã có (`chi-so-3`, `chi-so-4`…). */
export function maChiSoMoi(stats: readonly { id: string }[]): string {
  const co = new Set(stats.map((s) => s.id));
  for (let i = stats.length + 1; ; i += 1) {
    if (!co.has(`chi-so-${i}`)) return `chi-so-${i}`;
  }
}

/** Mã dòng xếp hạng mới chưa trùng (`dong-1`…). */
export function maDongMoi(rows: readonly { id: string }[]): string {
  const co = new Set(rows.map((r) => r.id));
  for (let i = rows.length + 1; ; i += 1) {
    if (!co.has(`dong-${i}`)) return `dong-${i}`;
  }
}

export type MaLoiPhanHe = 'trong' | 'trung' | 'sparkSai' | 'sparkThieu';

/**
 * Lỗi của bản nháp, khoá theo đường dẫn trường: `title`, `hero.label`, `hero.spark`, `stats.2.id`,
 * `rank.rows.0.value`… View tra đúng ô để hiện lỗi ngay dưới ô đó. Rỗng là gửi được.
 */
export function kiemBanNhap(b: BanNhapPhanHe): Record<string, MaLoiPhanHe> {
  const loi: Record<string, MaLoiPhanHe> = {};
  const trong = (s: string) => s.trim() === '';

  if (trong(b.title)) loi.title = 'trong';

  if (b.hero) {
    if (trong(b.hero.label)) loi['hero.label'] = 'trong';
    if (trong(b.hero.value)) loi['hero.value'] = 'trong';
    const spark = docSpark(b.hero.sparkChu);
    if (spark === null) loi['hero.spark'] = 'sparkSai';
    else if (spark.length < SPARK_TOI_THIEU) loi['hero.spark'] = 'sparkThieu';
  }

  const daGap = new Set<string>();
  b.stats.forEach((s, i) => {
    const id = s.id.trim();
    if (id === '') loi[`stats.${i}.id`] = 'trong';
    else if (daGap.has(id)) loi[`stats.${i}.id`] = 'trung';
    daGap.add(id);
    if (trong(s.label)) loi[`stats.${i}.label`] = 'trong';
    if (trong(s.value)) loi[`stats.${i}.value`] = 'trong';
  });

  if (b.rank) {
    if (trong(b.rank.title)) loi['rank.title'] = 'trong';
    b.rank.rows.forEach((r, i) => {
      if (trong(r.label)) loi[`rank.rows.${i}.label`] = 'trong';
      if (trong(r.value)) loi[`rank.rows.${i}.value`] = 'trong';
    });
  }
  return loi;
}

/** Chuỗi đã cắt khoảng trắng; rỗng thì vắng khoá (hợp đồng: khoá vắng thay cho `null`). */
function tuyChon<K extends string>(khoa: K, giaTri: string): Partial<Record<K, string>> {
  const s = giaTri.trim();
  return s === '' ? {} : ({ [khoa]: s } as Record<K, string>);
}

/**
 * Bản nháp → thân PUT. Gọi sau `kiemBanNhap` rỗng; đường xu hướng hỏng (không xảy ra khi đã kiểm) thành
 * dãy rỗng để server trả lỗi chứ không gửi `NaN`.
 */
export function thanPut(b: BanNhapPhanHe): UpdateModulePayload {
  const than: UpdateModulePayload = {
    title: b.title.trim(),
    ...tuyChon('documentsTitle', b.documentsTitle),
  };
  if (b.hero) {
    than.hero = {
      label: b.hero.label.trim(),
      value: b.hero.value.trim(),
      ...tuyChon('caption', b.hero.caption),
      ...tuyChon('delta', b.hero.delta),
      trend: b.hero.trend,
      spark: docSpark(b.hero.sparkChu) ?? [],
    };
  }
  if (b.stats.length > 0) {
    than.stats = b.stats.map(
      (s): Stat => ({
        id: s.id.trim(),
        label: s.label.trim(),
        value: s.value.trim(),
        ...tuyChon('delta', s.delta),
        trend: s.trend,
        accentName: s.accentName,
        iconName: s.iconName,
      }),
    );
  }
  if (b.rank) {
    than.rank = {
      title: b.rank.title.trim(),
      rows: b.rank.rows.map((r) => ({ id: r.id.trim(), label: r.label.trim(), value: r.value.trim() })),
    };
  }
  return than;
}

// ─────────────────────────── Tài liệu ───────────────────────────

/** Trần một tệp tải lên (`413 BAO_CAO_FILE_TOO_LARGE`). */
export const TOI_DA_MB = 50;

/** Backend chỉ nhận PDF và OOXML, kiểm magic bytes (`400 BAO_CAO_FILE_TYPE_NOT_SUPPORTED`). */
export const DUOI_NHAN = ['.pdf', '.docx', '.xlsx', '.pptx'] as const;

export type LoiTep = 'khongNhan' | 'quaLon' | 'rong';

export function duoiCua(tenTep: string): string {
  return /\.[^./\\]+$/.exec(tenTep)?.[0]?.toLowerCase() ?? '';
}

/** Kiểm tệp trước khi gửi; `null` là được. */
export function kiemTep(tep: TepChon): LoiTep | null {
  if (!(DUOI_NHAN as readonly string[]).includes(duoiCua(tep.name))) return 'khongNhan';
  if (tep.size === 0) return 'rong';
  if (tep.size > TOI_DA_MB * 1024 * 1024) return 'quaLon';
  return null;
}

/** Tài liệu lấy nội dung từ đâu: tệp tải lên (qua `open` ký URL) hay liên kết ngoài. */
export type NguonNoiDung = 'tep' | 'lien-ket';

export interface BanNhapTaiLieu {
  title: string;
  category: Category;
  iconName: IconName;
  accentName: AccentName;
  /** `null` là "Chưa phân loại". */
  folderId: string | null;
  summary: string;
  meta: string;
  nguonNoiDung: NguonNoiDung;
  /** Chỉ dùng khi `nguonNoiDung = 'lien-ket'`. */
  url: string;
  type: DocType;
}

export function banNhapTaiLieuMoi(folderId: string | null): BanNhapTaiLieu {
  return {
    title: '',
    category: 'other',
    iconName: 'file-text',
    accentName: 'primary',
    folderId,
    summary: '',
    meta: '',
    nguonNoiDung: 'tep',
    url: '',
    type: 'web',
  };
}

/** Tài liệu có tệp tải lên: backend gắn `fileName`, và `url` chỉ là địa chỉ `open` cố định. */
export function coTep(d: Pick<DocumentItem, 'fileName'>): boolean {
  return d.fileName !== undefined;
}

export function tuTaiLieu(d: DocumentItem): BanNhapTaiLieu {
  const tep = coTep(d);
  return {
    title: d.title,
    category: d.category,
    iconName: d.iconName,
    accentName: d.accentName,
    folderId: d.folderId ?? null,
    summary: d.summary ?? '',
    meta: d.meta ?? '',
    nguonNoiDung: tep ? 'tep' : 'lien-ket',
    // Không đưa địa chỉ `open` cố định vào ô URL: gửi nó lên là bỏ tệp đang gắn.
    url: tep ? '' : d.url,
    type: tep ? 'web' : d.type,
  };
}

/** Liên kết ngoài phải tuyệt đối `http`/`https` — đường tương đối làm `parse` cả payload của app thất bại. */
export function urlHopLe(url: string): boolean {
  try {
    const u = new URL(url.trim());
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname !== '';
  } catch {
    return false;
  }
}

export type MaLoiTaiLieu = 'trong' | 'urlSai' | 'chuaChonTep' | LoiTep;

/**
 * Lỗi của hộp tài liệu, khoá theo trường (`title`, `url`, `file`). `tepMoi` là tệp vừa chọn; `goc` là
 * tài liệu đang sửa (vắng khi tạo mới).
 */
export function kiemTaiLieu(
  b: BanNhapTaiLieu,
  tepMoi: TepChon | null,
  goc?: DocumentItem,
): Partial<Record<'title' | 'url' | 'file', MaLoiTaiLieu>> {
  const loi: Partial<Record<'title' | 'url' | 'file', MaLoiTaiLieu>> = {};
  if (b.title.trim() === '') loi.title = 'trong';
  if (b.nguonNoiDung === 'lien-ket') {
    if (b.url.trim() === '') loi.url = 'trong';
    else if (!urlHopLe(b.url)) loi.url = 'urlSai';
  } else if (tepMoi) {
    const l = kiemTep(tepMoi);
    if (l) loi.file = l;
  } else if (!goc || !coTep(goc)) {
    // Tạo mới, hoặc đổi tài liệu liên kết sang tệp: phải có tệp.
    loi.file = 'chuaChonTep';
  }
  return loi;
}

/** Thân tạo tài liệu: JSON (liên kết) hoặc trường `data` của multipart (tệp — không có `url`, `type`). */
export function thanTaoTaiLieu(b: BanNhapTaiLieu): DocumentFields {
  return {
    title: b.title.trim(),
    category: b.category,
    iconName: b.iconName,
    accentName: b.accentName,
    ...(b.folderId !== null ? { folderId: b.folderId } : {}),
    ...tuyChon('summary', b.summary),
    ...tuyChon('meta', b.meta),
    ...(b.nguonNoiDung === 'lien-ket' ? { url: b.url.trim(), type: b.type } : {}),
  };
}

/**
 * Thân `PATCH` — chỉ trường đã đổi so với `goc`.
 *
 * - `folderId` gửi khi đổi (`null` là đưa về "Chưa phân loại").
 * - `summary`, `meta` bị xoá trắng thì gửi chuỗi rỗng (backend coi là bỏ giá trị).
 * - `url`, `type` chỉ gửi ở chế độ liên kết, khi URL đổi hoặc khi tài liệu đang có tệp (đổi sang liên
 *   kết là bỏ tệp — đúng ý người dùng). Có tệp mới thì đi multipart, không bao giờ kèm `url`.
 */
export function thanSuaTaiLieu(b: BanNhapTaiLieu, goc: DocumentItem, coTepMoi: boolean): DocumentFields {
  const than: DocumentFields = {};
  const title = b.title.trim();
  if (title !== goc.title) than.title = title;
  if (b.category !== goc.category) than.category = b.category;
  if (b.iconName !== goc.iconName) than.iconName = b.iconName;
  if (b.accentName !== goc.accentName) than.accentName = b.accentName;
  if (b.folderId !== (goc.folderId ?? null)) than.folderId = b.folderId;
  const summary = b.summary.trim();
  if (summary !== (goc.summary ?? '')) than.summary = summary;
  const meta = b.meta.trim();
  if (meta !== (goc.meta ?? '')) than.meta = meta;

  if (b.nguonNoiDung === 'lien-ket' && !coTepMoi) {
    const url = b.url.trim();
    const dangCoTep = coTep(goc);
    if (dangCoTep || url !== goc.url) than.url = url;
    if (dangCoTep || b.type !== goc.type) than.type = b.type;
  }
  return than;
}
